/**
 * custom-quantity.js
 * ============================================================
 * Meter-based decimal quantity selector for Chandchakori.
 *
 * KEY DESIGN PRINCIPLE
 * --------------------
 * Shopify's cart API only accepts INTEGER quantities.
 * We therefore ALWAYS send quantity=1 (active) or quantity=0 (remove)
 * and store the real meter value in properties._meter_quantity.
 *
 * This completely eliminates the "Expected String to be a Integer: quantity"
 * error that occurs when decimal strings like "1.75" are sent as quantity.
 *
 * PUBLIC API
 * ----------
 * window.refreshCustomQuantityPrices() — call after any DOM re-render
 * window.getMeterValueForLine(lineIndex) — get meter value by cart line index
 */

/* ──────────────────────────────────────────────────────────────
   UTILITY HELPERS
   ────────────────────────────────────────────────────────────── */

/**
 * Parse a value to a float, returning 0 on failure.
 * @param {*} value
 * @returns {number}
 */
const toFloat = (value) => {
  const n = Number.parseFloat(value);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Round a value to 2 decimal places (avoids floating-point drift).
 * @param {number} value
 * @returns {number}
 */
const round2 = (value) => Math.round(value * 100) / 100;

/**
 * Format a number to exactly 2 decimal places as a string.
 * @param {number} value
 * @returns {string}
 */
const fmt2 = (value) => Number(value).toFixed(2);

/**
 * Snap a value to the nearest step multiple.
 * @param {number} value
 * @param {number} step
 * @returns {number}
 */
const snapToStep = (value, step) => {
  if (!step || step <= 0) return value;
  return round2(Math.round(value / step) * step);
};

/**
 * Clamp value between min and max (either can be null = unbounded).
 * @param {number} value
 * @param {number|null} min
 * @param {number|null} max
 * @returns {number}
 */
const clamp = (value, min, max) => {
  let v = value;
  if (min !== null && v < min) v = min;
  if (max !== null && v > max) v = max;
  return v;
};

/**
 * Format a money amount (in currency units, NOT cents) for display.
 * Uses Intl.NumberFormat for locale-aware formatting.
 * @param {number} amount  — e.g. 1687.50
 * @param {string} currency — ISO code e.g. "INR"
 * @returns {string} — e.g. "₹1,687.50"
 */
const formatMoney = (amount, currency) => {
  const val = Number(amount || 0);
  const lang = document.documentElement.lang || 'en-IN';
  const cur = currency || 'INR';
  try {
    return new Intl.NumberFormat(lang, {
      style: 'currency',
      currency: cur,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(val);
  } catch (_) {
    // Fallback for unsupported locales
    return `${cur} ${val.toFixed(2)}`;
  }
};

/* ──────────────────────────────────────────────────────────────
   ROOT ACCESSORS
   ────────────────────────────────────────────────────────────── */

/**
 * Read the current meter value from a custom-quantity root element.
 * Prefers the input value over the data attribute.
 * @param {HTMLElement} root
 * @returns {number}
 */
const getMeterValue = (root) => {
  const input = root.querySelector('.custom-quantity__input');
  if (input && input.value !== '') return toFloat(input.value);
  return toFloat(root.dataset.meterValue || root.dataset.min || 1);
};

/**
 * Read the price in cents from a root element.
 * @param {HTMLElement} root
 * @returns {number} — price in cents (integer)
 */
const getPriceCents = (root) => Number(root.dataset.priceCents || 0);

/**
 * Read the currency code from a root element.
 * @param {HTMLElement} root
 * @returns {string}
 */
const getCurrency = (root) => root.dataset.currency || 'INR';

/* ──────────────────────────────────────────────────────────────
   CART SUMMARY TOTALS
   ────────────────────────────────────────────────────────────── */

/**
 * Recalculate and update all cart totals displayed on the page
 * by summing line totals from all custom-quantity cart-mode widgets,
 * then subtracting any Shopify-applied discounts.
 *
 * The discount amount is stored server-side in data-cart-discount-cents
 * (in smallest currency unit — paise for INR, cents for USD, etc.)
 * so we can compute: estimated_total = sum(line_totals) - total_discounts
 *
 * Example:
 *   Meter = 1.50, Price = ₹750 → line total = ₹1,125
 *   Shopify discount (15%) on qty=1 = ₹112.50
 *   Estimated total = ₹1,125 − ₹112.50 = ₹1,012.50  ✓
 *
 * Called after every meter value change in cart mode.
 */
const updateCartSummaryTotals = () => {
  const cartWidgets = Array.from(
    document.querySelectorAll('[data-custom-quantity][data-cart-mode="true"]')
  );

  if (cartWidgets.length === 0) return;

  // ── Step 1: Sum all JS-calculated meter-based line totals ──
  const grandTotalCents = cartWidgets.reduce((sum, root) => {
    const meters = getMeterValue(root);
    const priceCents = getPriceCents(root);
    // Round to avoid floating-point drift (e.g. 1.75 × 75000 = 131250.0000000001)
    return sum + Math.round(meters * priceCents * 100) / 100;
  }, 0);

  // Get currency code from first widget
  const currency = getCurrency(cartWidgets[0]);

  // ── Step 2: Subtract Shopify's reported discount ───────────
  // The discount amount is stored in data-cart-discount-cents on the
  // .totals__total-value element. It is set server-side by Liquid as:
  //   cart.items_subtotal_price - cart.total_price
  // which equals the total of all cart-level + line-item discounts.
  //
  // We subtract this same absolute discount amount from our meter-based total
  // so the estimated total accounts for active discount codes/rules.
  document.querySelectorAll('.totals__total-value').forEach((el) => {
    const discountCents = Math.round(Number(el.dataset.cartDiscountCents || 0));
    // JS line total (paise) minus discount (paise), converted to rupees
    const finalTotal = Math.max(0, (grandTotalCents - discountCents)) / 100;
    el.textContent = formatMoney(finalTotal, el.dataset.currency || currency);
  });
};

/* ──────────────────────────────────────────────────────────────
   DISPLAY UPDATER
   ────────────────────────────────────────────────────────────── */

/**
 * Update all visual elements inside a custom-quantity root to reflect
 * the given meter value and price.
 *
 * @param {HTMLElement} root     — the [data-custom-quantity] element
 * @param {number}      value    — the new meter value (already validated)
 * @param {number}      priceCents — unit price in cents
 */
const updateDisplay = (root, value, priceCents) => {
  const meters      = round2(value);
  const currency    = getCurrency(root);
  const min         = toFloat(root.dataset.min || 1);
  const max         = root.dataset.max ? toFloat(root.dataset.max) : null;
  const outOfStock  = root.dataset.outOfStock === 'true';

  // ── Update data attribute (source of truth for JS reads) ──
  root.dataset.meterValue = fmt2(meters);

  // ── Input field ──
  const input = root.querySelector('.custom-quantity__input');
  if (input) input.value = fmt2(meters);

  // ── Quantity display label ──
  const qtyDisplay = root.querySelector('.custom-quantity__quantity-display');
  if (qtyDisplay) qtyDisplay.textContent = fmt2(meters);

  // ── Subtotal ──
  const subtotalEl = root.querySelector('.custom-quantity__subtotal-value');
  if (subtotalEl) {
    const subtotal = meters * (priceCents / 100);
    subtotalEl.textContent = formatMoney(subtotal, currency);
  }

  // ── Line total on the cart row ──
  const cartRow = root.closest('.cart-item');
  if (cartRow) {
    // Target both mobile and desktop price cells
    cartRow
      .querySelectorAll(
        '.cart-item__price-wrapper .price.price--end, ' +
        '.cart-item__final-price, ' +
        '.cart-item__price-wrapper span.price'
      )
      .forEach((el) => {
        el.textContent = formatMoney(meters * (priceCents / 100), currency);
      });
  }

  // ── Button disabled states ──
  const minusBtn = root.querySelector('.custom-quantity__button--minus');
  const plusBtn  = root.querySelector('.custom-quantity__button--plus');

  if (minusBtn) {
    minusBtn.disabled = outOfStock || round2(meters) <= round2(min);
  }
  if (plusBtn) {
    plusBtn.disabled = outOfStock || (max !== null && round2(meters) >= round2(max));
  }

  // ── Status note ──
  const note = root.querySelector('.custom-quantity__note');
  if (note) {
    if (outOfStock) {
      note.textContent = 'Out of Stock';
      note.style.color = 'rgb(var(--color-error, 186 0 0))';
    } else if (max !== null && round2(meters) >= round2(max)) {
      note.textContent = `Available Stock: ${fmt2(max)} Meter`;
      note.style.color = '';
    } else if (max !== null && max > 0) {
      note.textContent = `Available Stock: ${fmt2(max)} Meter`;
      note.style.color = '';
    } else if (root.dataset.inventoryText) {
      note.textContent = root.dataset.inventoryText;
      note.style.color = '';
    } else {
      note.textContent = '';
      note.style.color = '';
    }
  }

  // ── Update cart summary totals (cart page / cart drawer) ──
  updateCartSummaryTotals();
};

/* ──────────────────────────────────────────────────────────────
   VALUE SETTER
   ────────────────────────────────────────────────────────────── */

/**
 * Validate, snap, clamp, and apply a new meter value to a root element.
 *
 * @param {HTMLElement} root
 * @param {number}      nextValue — raw desired value (may be out of range)
 * @returns {number} — the final applied value
 */
const setValue = (root, nextValue) => {
  const min  = toFloat(root.dataset.min || 1);
  const max  = root.dataset.max ? toFloat(root.dataset.max) : null;
  const step = toFloat(root.dataset.step || 0.25);

  // 1. Snap to step grid
  const snapped = snapToStep(nextValue, step);

  // 2. Clamp between min and max
  const clamped = clamp(snapped, min, max);

  // 3. Ensure 2-decimal precision
  const final = round2(clamped);

  // 4. Push to DOM
  updateDisplay(root, final, getPriceCents(root));

  return final;
};

/* ──────────────────────────────────────────────────────────────
   CART AJAX UPDATE
   ────────────────────────────────────────────────────────────── */

/**
 * Send a cart change request for a cart-mode widget.
 *
 * IMPORTANT: We always send quantity=1 (integer) to Shopify.
 * The real meter value is stored in properties._meter_quantity.
 * This prevents the "Expected String to be a Integer: quantity" error.
 *
 * @param {HTMLElement} root        — the [data-custom-quantity] element
 * @param {number}      meterValue  — the validated meter value (e.g. 1.75)
 */
const updateCart = (root, meterValue) => {
  // Only fire for cart-mode widgets that have a line index
  if (root.dataset.cartMode !== 'true' || !root.dataset.line) return;

  const lineIndex = root.dataset.line;

  // Find the parent CartItems or CartDrawerItems custom element
  const cartItems =
    root.closest('cart-items') || root.closest('cart-drawer-items');

  if (!cartItems || typeof cartItems.updateQuantity !== 'function') return;

  // Create a synthetic event object (CartItems.updateQuantity expects one)
  const input = root.querySelector('.custom-quantity__input');
  const syntheticEvent = {
    currentTarget: input,
    target: input,
    preventDefault() {},
    stopImmediatePropagation() {},
  };

  // Calculate 0.25m unit steps to send to Shopify API
  // 1 step = 0.25 Meter
  // 1.00 Meter = 4 units (4 × ₹187.50 = ₹750.00)
  // 1.75 Meter = 7 units (7 × ₹187.50 = ₹1,312.50)
  // 2.25 Meter = 9 units (9 × ₹187.50 = ₹1,687.50)
  const step = toFloat(root.dataset.step || 0.25);
  const shopifyUnits = Math.max(1, Math.round(meterValue / step));

  // Pass calculated integer quantity to cartItems.updateQuantity
  cartItems.updateQuantity(
    lineIndex,
    shopifyUnits,     // ← 0.25m unit steps (e.g. 9 for 2.25 meters)
    syntheticEvent,
    input ? input.getAttribute('name') : null,
    root.dataset.variantId || null,
    meterValue        // ← exact decimal string stored in properties._meter_quantity
  );
};

/* ──────────────────────────────────────────────────────────────
   INITIALIZATION
   ────────────────────────────────────────────────────────────── */

/**
 * Initialize a single custom-quantity root element.
 * Guards against double-initialization.
 *
 * @param {HTMLElement} root
 */
const initializeRoot = (root) => {
  if (!root || root.dataset.initialized === 'true') return;
  root.dataset.initialized = 'true';

  // Read the initial value from the input or data attribute
  const input = root.querySelector('.custom-quantity__input');
  const initialRaw = input ? (input.value || root.dataset.meterValue) : root.dataset.meterValue;
  const initialValue = toFloat(initialRaw || root.dataset.min || 1);

  // Apply initial display (does NOT trigger cart update)
  setValue(root, initialValue);
};

/**
 * Initialize all custom-quantity widgets currently in the DOM.
 */
const initializeAll = () => {
  document.querySelectorAll('[data-custom-quantity]').forEach(initializeRoot);
};

/* ──────────────────────────────────────────────────────────────
   CHANGE HANDLER (shared by button clicks and manual input)
   ────────────────────────────────────────────────────────────── */

/**
 * Handle a quantity change: validate → set display → sync cart.
 *
 * @param {HTMLElement} root
 * @param {number}      nextValue
 */
const handleQuantityChange = (root, nextValue) => {
  const finalValue = setValue(root, nextValue);
  // Only fire AJAX update in cart mode
  updateCart(root, finalValue);
  return finalValue;
};

/* ──────────────────────────────────────────────────────────────
   EVENT LISTENERS
   ────────────────────────────────────────────────────────────── */

// ── Button clicks (+/-) ──────────────────────────────────────
document.addEventListener('click', (event) => {
  const button = event.target.closest('.custom-quantity__button');
  if (!button) return;

  const root = button.closest('[data-custom-quantity]');
  if (!root || root.dataset.initialized !== 'true') return;
  if (button.disabled) return;

  const input = root.querySelector('.custom-quantity__input');
  const currentValue = toFloat(input ? input.value : root.dataset.meterValue || root.dataset.min || 1);
  const step = toFloat(root.dataset.step || 0.25);

  const delta = button.dataset.action === 'increase' ? step : -step;
  const nextValue = round2(currentValue + delta);

  handleQuantityChange(root, nextValue);
});

// ── Live typing (input event) ────────────────────────────────
// Keep only digits and decimal point while typing; don't trigger cart update yet
document.addEventListener('input', (event) => {
  const input = event.target;
  if (!input.classList.contains('custom-quantity__input')) return;

  const root = input.closest('[data-custom-quantity]');
  if (!root) return;

  // Strip invalid characters
  const sanitized = input.value.replace(/[^0-9.]/g, '');
  if (input.value !== sanitized) input.value = sanitized;

  // Update display (price only) without triggering cart update
  const value = toFloat(sanitized || root.dataset.min || 1);
  if (Number.isFinite(value) && value > 0) {
    setValue(root, value);
    // Note: cart update fires on 'change' (blur/enter), not on every keystroke
  }
});

// ── Committed value change (blur / Enter) ────────────────────
document.addEventListener('change', (event) => {
  const input = event.target;
  if (!input.classList.contains('custom-quantity__input')) return;

  const root = input.closest('[data-custom-quantity]');
  if (!root) return;

  const value = toFloat(input.value || root.dataset.min || 1);
  handleQuantityChange(root, value);
});

/* ──────────────────────────────────────────────────────────────
   MUTATION OBSERVER — auto-init newly injected widgets
   ────────────────────────────────────────────────────────────── */
const quantityObserver = new MutationObserver(() => {
  document.querySelectorAll('[data-custom-quantity]').forEach((root) => {
    if (root.dataset.initialized !== 'true') initializeRoot(root);
  });
});

quantityObserver.observe(document.body, { childList: true, subtree: true });

/* ──────────────────────────────────────────────────────────────
   BOOT
   ────────────────────────────────────────────────────────────── */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeAll);
} else {
  initializeAll();
}

/* ──────────────────────────────────────────────────────────────
   PUBLIC API
   ────────────────────────────────────────────────────────────── */

/**
 * Refresh all custom-quantity display values.
 * Call this after any DOM section re-render (cart AJAX response, etc.)
 */
window.refreshCustomQuantityPrices = () => {
  document.querySelectorAll('[data-custom-quantity]').forEach((root) => {
    // Re-initialize newly injected widgets
    if (root.dataset.initialized !== 'true') {
      initializeRoot(root);
    } else {
      // Refresh display for existing initialized widgets
      const meters = getMeterValue(root);
      updateDisplay(root, meters, getPriceCents(root));
    }
  });
};

/**
 * Get the meter value for a specific cart line index.
 * Useful for external scripts that need to read the current meter quantity.
 *
 * @param {number|string} lineIndex — 1-based cart line index
 * @returns {number|null} — meter value or null if not found
 */
window.getMeterValueForLine = (lineIndex) => {
  const root = document.querySelector(
    `[data-custom-quantity][data-line="${lineIndex}"]`
  );
  return root ? getMeterValue(root) : null;
};
